/**
 * Voice call readiness — two toggles in this file only (not per environment):
 * - `VOICE_READINESS_METRICS_ENABLED` — run checks + emit `VOICE_READINESS_*` analytics
 * - `VOICE_READINESS_UI_ENFORCEMENT_ENABLED` — on failure, disable call / show readiness copy / open-request fallback (requires metrics on)
 *
 * Uses offline detection, Network Information API (where available), and `ping()` RTT.
 * Twilio Device.runPreflight(token) needs a JWT; run after requestVoice if you need ICE-level checks.
 */
import { ping } from '@/common/api/api.pleased';
import { analytics, VOICE_EVENTS } from '@/common/logs';

export type VoiceConnectionQuality = 'offline' | 'poor' | 'slow' | 'ok';

/** Toggle 1: readiness checks and analytics. */
export const VOICE_READINESS_METRICS_ENABLED = true;

/** Toggle 2: block call + show fallback UI when the check fails (only if metrics are on). */
export const VOICE_READINESS_UI_ENFORCEMENT_ENABLED = false;

export const isVoiceReadinessTrackingEnabled = (): boolean => VOICE_READINESS_METRICS_ENABLED;

export const isVoiceReadinessEnforcementEnabled = (): boolean =>
  VOICE_READINESS_METRICS_ENABLED && VOICE_READINESS_UI_ENFORCEMENT_ENABLED;

type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string;
    rtt?: number;
    downlink?: number;
  };
};

const measureApiRtt = async (): Promise<{ rttMs: number; ok: boolean }> => {
  const start = performance.now();
  try {
    await ping();
    return { rttMs: performance.now() - start, ok: true };
  } catch {
    return { rttMs: performance.now() - start, ok: false };
  }
};

/**
 * Layered signal: offline → connection hints → active probe to Pleased API (same stack as chat).
 */
export const getConnectionQuality = async (): Promise<{
  quality: VoiceConnectionQuality;
  rttMs?: number;
  networkHint?: string;
}> => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { quality: 'offline' };
  }

  const conn = (navigator as NavigatorWithConnection).connection;
  if (conn) {
    const rtt = conn.rtt;
    if (conn.effectiveType === 'slow-2g' || (typeof rtt === 'number' && rtt > 800)) {
      return { quality: 'poor', networkHint: conn.effectiveType };
    }
    if (conn.effectiveType === '2g' || (typeof rtt === 'number' && rtt > 400)) {
      return { quality: 'slow', networkHint: conn.effectiveType };
    }
  }

  const { rttMs, ok } = await measureApiRtt();
  if (!ok) {
    return { quality: 'poor', rttMs };
  }
  if (rttMs > 1000) {
    return { quality: 'poor', rttMs };
  }
  if (rttMs > 400) {
    return { quality: 'slow', rttMs };
  }
  return { quality: 'ok', rttMs };
};

/**
 * @returns ok — whether the call option should be enabled (allows `slow`; blocks `offline` and `poor`).
 */
export const runVoiceReadinessCheck = async (): Promise<{
  ok: boolean;
  quality: VoiceConnectionQuality;
  rttMs?: number;
  networkHint?: string;
}> => {
  const { quality, rttMs, networkHint } = await getConnectionQuality();
  const ok = quality !== 'offline' && quality !== 'poor';

  analytics.trackVoiceEvent({
    name: VOICE_EVENTS.VOICE_READINESS_CHECK,
    metadata: {
      chatbotSessionId: analytics.sessionId,
      timestamp: Date.now(),
      quality,
      rttMs,
      networkHint,
      pass: ok,
    },
  });

  if (!ok) {
    analytics.trackVoiceEvent({
      name: VOICE_EVENTS.VOICE_READINESS_FAILED,
      metadata: {
        chatbotSessionId: analytics.sessionId,
        timestamp: Date.now(),
        quality,
        rttMs,
        networkHint,
      },
    });
  }

  return { ok, quality, rttMs, networkHint };
};
